/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2020, Zumper
 * @description   getShouldLimitLeads
 */

import type { BuildingListingCollection } from '@zcode/bizos'
import type { State } from '@zcode/state-type'
import { isAfter, subDays } from 'date-fns'
import { getMessaged } from '../../../../inspectors/index'
import { logger } from '../../../../logger'

export const LONG_TERM_CUTOFF_DAYS = 30
export const SHORT_TERM_CUTOFF_DAYS = 7

export const SHORT_TERM_LEAD_LIMIT = 20
export const LONG_TERM_LEAD_LIMIT = 50

type GetShouldLimitLeadsType = (arg: {
  longTermLeadCount?: number
  shortTermLeadCount?: number
}) => boolean
// takes in messaged as an Immutable Record
export const getShouldLimitLeads: GetShouldLimitLeadsType = ({
  longTermLeadCount,
  shortTermLeadCount,
} = {}) => {
  if (!longTermLeadCount && !shortTermLeadCount) {
    return false
  }
  const isShortTermLeadCount = !!shortTermLeadCount && shortTermLeadCount >= SHORT_TERM_LEAD_LIMIT
  const isLongTermLeadCount = !!longTermLeadCount && longTermLeadCount >= LONG_TERM_LEAD_LIMIT
  // this is the minimum number of leads currently to qualify for LIMIT_LEADS
  return isShortTermLeadCount || isLongTermLeadCount
}

type GetRecentLeadCountsType = (
  messaged: BuildingListingCollection,
  refData?: Date
) => { longTermLeadCount: number; shortTermLeadCount: number }
export const getRecentLeadCounts: GetRecentLeadCountsType = (messaged, refDate = new Date()) => {
  if (!messaged) {
    return { longTermLeadCount: 0, shortTermLeadCount: 0 }
  }

  let longTermLeadCount = 0
  let shortTermLeadCount = 0
  try {
    const listings = messaged.listings
    const buildings = messaged.buildings
    if (Object.keys(listings).length === 0 && Object.keys(buildings).length === 0) {
      return { longTermLeadCount, shortTermLeadCount }
    }

    const shortTermCutoff = subDays(refDate, SHORT_TERM_CUTOFF_DAYS)
    const longTermCutoff = subDays(refDate, LONG_TERM_CUTOFF_DAYS)

    for (const value of Object.values(listings)) {
      if (!value) {
        continue
      }
      if (isAfter(new Date(value * 1000), shortTermCutoff)) {
        shortTermLeadCount += 1
        longTermLeadCount += 1
      } else if (isAfter(new Date(value * 1000), longTermCutoff)) {
        longTermLeadCount += 1
      }
    }

    for (const value of Object.values(buildings)) {
      if (!value) {
        continue
      }
      if (isAfter(new Date(value * 1000), longTermCutoff)) {
        longTermLeadCount += 1
      }
      if (isAfter(new Date(value * 1000), shortTermCutoff)) {
        shortTermLeadCount += 1
      }
    }
    return { longTermLeadCount, shortTermLeadCount }
  } catch (e) {
    logger.error('Failed to get lead count for limiting', e)
    return { longTermLeadCount: 0, shortTermLeadCount: 0 }
  }
}

export const selectShouldLimitLeads = (state: State) =>
  getShouldLimitLeads(getRecentLeadCounts(getMessaged(state)))
